import Vue from 'vue';
import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import _ from 'lodash';
import Api from '@/store/blockbusterApi';
import store from '@/store';
import MetadataModel from '@/models/MetadataModel';
import { MetadataResponse } from '@/types/blockbusterResponses';
import { merge, concat } from 'lodash';
import Axios from 'axios';
import dayjs from 'dayjs';
import _default from 'vuex';

interface SortSettings {
  key: keyof MetadataModel,
  reverse: boolean,
}

interface FlagOptions {
  id: string,
  reason: string,
  flagged?: boolean;
}

@Module({
  name: 'metadata',
  namespaced: true,
  dynamic: true,
  store
})
export class MetadataModule extends VuexModule {
  _values: Array<MetadataModel> = [];
  prev = '';
  next = '';
  searchString = '';
  sortSettings: SortSettings = {
    key: 'id',
    reverse: true,
  };

  get sorted(): Array<MetadataModel> {
    const values = [...this._values];
    const key = this.sortSettings.key;

    return values.sort((a, b) => {
      let val = 0;
      const propA = a[key] || '';
      const propB = b[key] || '';

      if (propA < propB) {
        val = -1;
      }
      if (propB < propA) {
        val = 1;
      }
      return !!this.sortSettings && this.sortSettings.reverse ? val * -1 : val;
    });
  }

  get filtered(): Array<MetadataModel> {
    if (this.searchString === '') {
      return this.sorted;
    }

    const searchTerms = this.searchString.split(',').map(term => term.trim());

    const compare = (targetString: string): boolean => {
      const match = searchTerms.find(term => targetString.includes(term));
      return !!match;
    };

    const checkForMatch = (value: any): boolean => {
      if (!value) {
        return false;
      }

      if (value instanceof Array) {
        const foundMatch = value.find(text => compare(text.toString()));
        return !!foundMatch;
      }

      return compare(value.toString());
    };

    return this.sorted.filter(e => {
      for (const prop in e) {
        const value = _.get(e, prop);
        const hasMatch = checkForMatch(value);
        if (hasMatch) {
          return true;
        }
      }

      return false;
    });
  }

  @Mutation
  receiveMetadata(data: MetadataResponse): void {
    Vue.set(this, '_values', concat(this._values, data.metadata.map((v, i) => {
      return {
        ...v,
        index: i,
      };
    })));

    this.prev = data.links.prev;
  }

  @Mutation
  sort(key: keyof MetadataModel): void {
    if (!!this.sortSettings && this.sortSettings.key === key) {
      this.sortSettings.reverse = !this.sortSettings.reverse;
    } else {
      this.sortSettings = {
        key,
        reverse: false,
      };
    }
  }

  @Mutation
  search(searchstring: string): void {
    this.searchString = searchstring;
  }


  @Mutation
  updateMetadata(fields: MetadataModel): void {
    const newMetadata = this._values.find(v => v.id === fields.id);
    if (newMetadata) {
      const index = this._values.indexOf(newMetadata);
      this._values[index] = merge(newMetadata, fields);
      this._values = Vue.set(this, '_values', concat([], this._values));
    }
  }

  @Mutation
  reset(): void {
    this._values = [];
    this.prev = '';
    this.next = '';
    this.searchString = '';
    this.sortSettings = {
      key: 'id',
      reverse: true,
    };
  }

  @Action({ commit: 'search' })
  updateSearch(searchText: string): string {
    return searchText;
  }

  @Action({ commit: 'sort' })
  sortBy(key: string): string {
    return key;
  }

  @Action({ commit: 'receiveMetadata', rawError: true })
  async getMetadata(url?: string): Promise<MetadataResponse> {
    let data;
    if (url) {
      data = await Api.makeGetRequest(url);
    } else {
      data = await Api.fetchMetadata();
    }

    // format date
    for (let i = 0; i < data.metadata.length; i++) {
      const metadataEntry = data.metadata[i];

      metadataEntry.uploadedTimestamp = dayjs(metadataEntry.uploadedTimestamp).format('MMM-DD-YYYY HH:mm');
      metadataEntry.recordedTimestamp = dayjs(metadataEntry.recordedTimestamp).format('MMM-DD-YYYY HH:mm');
    }

    return data;
  }

  @Action({})
  async getPrevMetadata(): Promise<MetadataResponse> {
    const response = await this.getMetadata(this.prev);
    return response;
  }

  @Action({ commit: 'updateMetadata' })
  async flagMetadata(flagOptions: FlagOptions): Promise<FlagOptions> {
    const { id, reason } = flagOptions;
    await Api.patchMetadata(id, { flagged: true, reason: reason });
    return { id, reason, flagged: true };
  }

  @Action
  async downloadVideos(filenames: Array<string>): Promise<void> {
    for (const filename of filenames) {
      const url = await Api.getSignedURL(filename);

      const response = await Axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob)
      link.download = filename;
      link.click();
    }
  }
}

export default getModule(MetadataModule);
